import { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { useToast } from "@/hooks/use-toast";
import { KeyRound, Shield, AlertTriangle, Lock, Sparkles, ArrowRight } from "lucide-react";
import zxcvbn from "zxcvbn";

interface CreatePasswordSplashProps {
  open: boolean;
  onComplete: () => void;
  userName?: string;
}

export function CreatePasswordSplash({ open, onComplete, userName }: CreatePasswordSplashProps) {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const strength = useMemo(() => {
    if (!password) return null;
    return zxcvbn(password);
  }, [password]);

  const strengthScore = strength?.score ?? 0;
  const strengthLabel = useMemo(() => {
    if (!password) return "";
    if (strengthScore <= 1) return "Very Weak";
    if (strengthScore === 2) return "Weak";
    if (strengthScore === 3) return "Fair";
    return "Strong";
  }, [password, strengthScore]);

  const strengthColors = useMemo(() => {
    if (strengthScore <= 1) return "bg-red-500";
    if (strengthScore === 2) return "bg-orange-500";
    if (strengthScore === 3) return "bg-yellow-500";
    return "bg-green-500";
  }, [strengthScore]);

  const isStrongEnough = password.length >= 8 && strengthScore >= 3;
  const matches = password && confirmPassword && password === confirmPassword;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!matches) {
      toast({
        title: "Passwords don't match",
        description: "Please make sure both passwords are the same.",
        variant: "destructive",
      });
      return;
    }

    if (!isStrongEnough) {
      toast({
        title: "Weak password",
        description: "Use at least 8 characters with a stronger mix of words, numbers, and symbols.",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) throw error;

      toast({
        title: "Password created!",
        description: "You can now use this password to sign in next time.",
      });

      onComplete();
    } catch (err: any) {
      toast({
        title: "Failed to set password",
        description: err?.message ?? "Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSkip = () => {
    onComplete();
  };

  if (!open) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center bg-background/95 backdrop-blur-sm"
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          transition={{ delay: 0.1 }}
          className="w-full max-w-md mx-4"
        >
          <div className="relative overflow-hidden rounded-2xl border bg-card shadow-2xl">
            {/* Decorative gradient header */}
            <div className="absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-primary/20 via-primary/10 to-transparent" />
            
            <div className="relative p-8">
              {/* Icon and title */}
              <div className="text-center mb-8">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-4">
                  <Lock className="h-8 w-8 text-primary" />
                </div>
                <h2 className="text-2xl font-bold mb-2">
                  Secure Your Account{userName ? `, ${userName.split(' ')[0]}` : ''}
                </h2>
                <p className="text-muted-foreground">
                  Create a password so you can sign in anytime to view your inquiries and manage your events.
                </p>
              </div>

              {/* Benefits */}
              <div className="flex items-center justify-center gap-6 mb-8 text-sm text-muted-foreground">
                <div className="flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-primary" />
                  <span>Track inquiries</span>
                </div>
                <div className="flex items-center gap-2">
                  <Shield className="h-4 w-4 text-primary" />
                  <span>Secure access</span>
                </div>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="new-password">New Password</Label>
                  <div className="relative">
                    <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="new-password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="pl-10"
                      placeholder="Enter a strong password"
                      minLength={8}
                      autoComplete="new-password"
                    />
                  </div>
                  
                  {password.length > 0 && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      className="space-y-2 pt-2"
                    >
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-muted-foreground">Password strength</span>
                        <span className={strengthScore < 3 ? "text-destructive" : "text-primary"}>
                          {strengthLabel}
                        </span>
                      </div>
                      <div className="h-2 rounded-full bg-muted overflow-hidden">
                        <motion.div
                          initial={{ width: 0 }}
                          animate={{ width: `${(strengthScore + 1) * 20}%` }}
                          className={`h-full ${strengthColors} transition-colors`}
                        />
                      </div>
                      {strength?.feedback?.warning && strengthScore < 3 && (
                        <div className="flex items-start gap-2 rounded-md border bg-muted/50 p-2">
                          <AlertTriangle className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                          <p className="text-xs text-muted-foreground">{strength.feedback.warning}</p>
                        </div>
                      )}
                      {strengthScore >= 3 && (
                        <div className="flex items-start gap-2 rounded-md border bg-primary/5 p-2">
                          <Shield className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                          <p className="text-xs text-primary">Looks good!</p>
                        </div>
                      )}
                    </motion.div>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirm-password">Confirm Password</Label>
                  <div className="relative">
                    <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="confirm-password"
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="pl-10"
                      placeholder="Confirm your password"
                      minLength={8}
                      autoComplete="new-password"
                    />
                  </div>
                  {confirmPassword.length > 0 && password !== confirmPassword && (
                    <p className="text-xs text-destructive">Passwords don't match</p>
                  )}
                  {confirmPassword.length > 0 && password === confirmPassword && (
                    <p className="text-xs text-primary">Passwords match ✓</p>
                  )}
                </div>

                <Button 
                  type="submit" 
                  className="w-full gap-2" 
                  size="lg"
                  disabled={isLoading || !matches || !isStrongEnough}
                >
                  {isLoading ? (
                    "Creating..."
                  ) : (
                    <>
                      Create Password
                      <ArrowRight className="h-4 w-4" />
                    </>
                  )}
                </Button>
              </form>

              {/* Skip option */}
              <div className="mt-6 text-center">
                <button
                  onClick={handleSkip}
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors underline-offset-4 hover:underline"
                >
                  Skip for now
                </button>
                <p className="text-xs text-muted-foreground mt-1">
                  You can set a password later from your dashboard
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
